import { FieldSourceKind, type SourcingEventDto, type SourcingEventFieldSourceDto } from "../../autogen/bff-api";
import { basisFieldsAsked } from "../procurement/procedure";
import {
  type EventInclusions,
  type OrgWizardAccess,
  type PublicEventStep,
  resolveInclusions,
  resolveProcess,
} from "../sourcing-events/buyer/wizardSteps";

export type CreationArea = "basis" | "regulation" | "qualification" | "criteria" | "requirements" | "deadlines" | "invitations";

export type AreaStatus = "done" | "filledByAi" | "remaining";

type AreaRule = {
  area: CreationArea;
  paths: string[];
  filled: (event: SourcingEventDto, access: OrgWizardAccess) => boolean;
  step?: PublicEventStep;
  alsoInPrivateCompetitions?: boolean;
  formSteps?: PublicEventStep[];
};

const AREA_RULES: AreaRule[] = [
  {
    area: "basis",
    paths: ["eventData.title", "eventData.description", "estimatedValue", "contractNature"],
    formSteps: ["info", "details"],
    filled: (event, access) => {
      const context = event.eventContext;
      const asked = basisFieldsAsked(access.hasPublicProcurementAccess, context.type === "Public" ? context : null);
      const valueSaved = "estimatedValue" in context && !!context.estimatedValue;
      const typeSaved = "contractNature" in context && !!context.contractNature;
      return (
        !!event.eventData.title?.trim() && (!asked.estimatedValue || valueSaved) && (!asked.contractType || typeSaved)
      );
    },
  },
  {
    area: "regulation",
    paths: ["regulation", "foaPart", "procedureType"],
    formSteps: ["classification", "procedure", "info"],
    filled: (event) => hasSettledRegulation(event),
  },
  {
    area: "qualification",
    paths: ["selectionCriteria"],
    step: "qualification",
    alsoInPrivateCompetitions: true,
    filled: (event) => event.eventData.selectionCriteria.length > 0,
  },
  {
    area: "criteria",
    paths: ["awardCriteria"],
    step: "awardCriteria",
    filled: (event) => !!(event.eventData.awardCriteria?.lowestPrice || event.eventData.awardCriteria?.bestPriceQualityRatio),
  },
  {
    area: "requirements",
    paths: ["minimumRequirements"],
    step: "minimumRequirements",
    filled: (event) => event.eventData.minimumRequirements.length > 0,
  },
  {
    area: "deadlines",
    paths: ["bidSubmissionDeadline", "requestDeadline"],
    formSteps: ["procedure", "details"],
    filled: (event) => !!(event.eventData.bidSubmissionDeadline || event.eventData.requestDeadline),
  },
  {
    area: "invitations",
    paths: ["organizationInvites"],
    filled: (event) =>
      (event.eventContext.type === "Private" || event.eventContext.type === "MiniCompetition") &&
      event.eventContext.organizationInvites.length > 0,
    step: "invitations",
  },
];

export function hasSettledRegulation(event: SourcingEventDto): boolean {
  const context = event.eventContext;
  return context.type === "Public" ? context.procedureType != null : context.type !== "Unclassified";
}

const inForm = (rule: AreaRule, steps: PublicEventStep[]) => !rule.step || steps.includes(rule.step);

const settledByUser = (source: SourcingEventFieldSourceDto) =>
  source.confirmedByUser || source.changedByUser || source.source.kind === FieldSourceKind.UserAnswer;

export const creationProgress = (
  event: SourcingEventDto | undefined,
  sources: SourcingEventFieldSourceDto[],
  access: OrgWizardAccess,
  { regulationAwaitsConfirmation = false }: { regulationAwaitsConfirmation?: boolean } = {},
): { area: CreationArea; status: AreaStatus }[] => {
  const steps = stepsAvailableTo(event, access);
  const isPublicBuyersPrivateCompetition =
    event?.eventContext.type === "Private" && access.hasPublicProcurementAccess;
  const applies = (rule: AreaRule) =>
    inForm(rule, steps) || (isPublicBuyersPrivateCompetition && !!rule.alsoInPrivateCompetitions);
  return AREA_RULES.filter(applies).map((rule) => {
    if (!event || !rule.filled(event, access)) return { area: rule.area, status: "remaining" };
    if (rule.area === "regulation" && regulationAwaitsConfirmation) return { area: rule.area, status: "filledByAi" };
    const areaSources = sources.filter((source) => rule.paths.some((path) => source.path.includes(path)));
    return { area: rule.area, status: areaSources.every(settledByUser) ? "done" : "filledByAi" };
  });
};

export type InterviewPosition = { step: PublicEventStep; done: boolean };

export const interviewPosition = (
  event: SourcingEventDto,
  sources: SourcingEventFieldSourceDto[],
  access: OrgWizardAccess,
): InterviewPosition => {
  const steps = stepsAvailableTo(event, access);
  const wizardSteps = stepsAvailableTo(event, access, resolveInclusions(event.eventData));
  const rule = AREA_RULES.find((candidate) => inForm(candidate, steps) && !candidate.filled(event, access));
  if (!rule) return { step: wizardSteps[wizardSteps.length - 1] ?? "summary", done: true };
  const candidates = rule.step ? [rule.step] : (rule.formSteps ?? []);
  return { step: candidates.find((step) => wizardSteps.includes(step)) ?? wizardSteps[0] ?? "info", done: false };
};

const stepsAvailableTo = (
  event: SourcingEventDto | undefined,
  access: OrgWizardAccess,
  inclusions?: EventInclusions,
): PublicEventStep[] => {
  const context = event?.eventContext;
  const process =
    (context &&
      resolveProcess(context.type, access, {
        procedureType: context.type === "Public" ? context.procedureType : undefined,
        inclusions,
      })) ??
    resolveProcess("Public", access);
  return (process?.steps ?? []).map((step) => step.key);
};
